import { useRef } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { billing } from '@/services/billing';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { hasTaxableAddress, TAX_DEBOUNCED_FIELDS, TAX_LOOKUP_DEBOUNCE_MS } from '../utils/tax-lookup-rules';
import { sleep } from '@/utils/sleep';

type TaxLookupTarget = Pick<CreditCardFormAddress, 'country' | 'state' | 'zipCode' | 'addressLine1'>;

function taxLookupTarget(address: CreditCardFormAddress): TaxLookupTarget {
  return {
    country: address.country,
    state: address.state,
    zipCode: address.zipCode,
    addressLine1: address.addressLine1,
  };
}

// The first lookup is a chosen address, not typed, so it does not wait out the debounce.
function isTypedChange(previous: TaxLookupTarget | null, next: TaxLookupTarget): boolean {
  return previous != null && TAX_DEBOUNCED_FIELDS.some((field) => previous[field] !== next[field]);
}

function creditsPurchaseTaxRateQueryKey(target: TaxLookupTarget, isAvalara: boolean | undefined): QueryKey {
  return ['creditsPurchaseTaxRate', target.country, target.state, target.zipCode, target.addressLine1, isAvalara];
}

export type CreditsPurchaseTaxRateOptions = {
  /** Passed to `billing.fetchTaxRate`. Leave it out to keep its `ordway_id` default. */
  isAvalara?: boolean;
};

export type CreditsPurchaseTaxRate = {
  rate: number | undefined;
  isFetching: boolean;
};

/**
 * A rate is only for the address on screen. The debounce runs inside the query, so `isFetching` also
 * blocks submit while the user types.
 *
 * The rate lives in the query, one entry per address, and never in `billing.taxRate`. That atom holds the
 * account's own rate from billing info, which the Billing overview reads, and these panels open over it.
 */
export function useCreditsPurchaseTaxRate(
  address: CreditCardFormAddress,
  options: CreditsPurchaseTaxRateOptions = {},
): CreditsPurchaseTaxRate {
  const target = taxLookupTarget(address);
  const isComplete = hasTaxableAddress(address);
  const lastTargetRef = useRef<TaxLookupTarget | null>(null);

  const query = useQuery({
    queryKey: creditsPurchaseTaxRateQueryKey(target, options.isAvalara),
    // Reading `signal` lets React Query cancel a run whose address is no longer on screen.
    queryFn: async ({ signal }) => {
      const isTyped = isTypedChange(lastTargetRef.current, target);
      lastTargetRef.current = target;
      if (isTyped) {
        await sleep(TAX_LOOKUP_DEBOUNCE_MS, signal);
        if (signal.aborted) {
          return null;
        }
      }
      const rate = await billing.fetchTaxRate({ ...target, isAvalara: options.isAvalara, signal });
      return rate?.rate ?? null;
    },
    enabled: isComplete,
    staleTime: 0,
    gcTime: 0,
  });

  return { rate: isComplete ? (query.data ?? undefined) : undefined, isFetching: isComplete && query.isFetching };
}
